import React, { useEffect, useState } from 'react';
import { Divider, HStack, VStack } from 'native-base';
import SelectLoyalty from '../dropdowns/SelectLoyalty';
import {
    useSpringBigStore,
    usePointsToCashGiftCard,
    observer,
    GiftCard,
    i18nFn,
    GIFTCARD_FLOWS, useTheme,
} from '@springbig/shared';
import { Caption, CaptionError } from '~/shared/Typography';
import { useToast } from '~/hooks/toast';
import PointsConverterField from '~/pages/giftCard/pointsConverter/PointsConverterField';
import { Button } from '~/shared/Theme';

const PointsConverter = ({ t }: { t: i18nFn }) => {

    const { member, merchant, giftCards } = useSpringBigStore();

    const [convertPoints, setConvertPoints] = React.useState(0);
    const [pointsError, setPointsError] = React.useState('');
    const [loyaltyCardError, setLoyaltyCardError] = React.useState('');
    const [submitError, setSubmitError] = React.useState('');
    const [loyaltyCard, setLoyaltyCard] = React.useState<GiftCard | null>(null);

    const {
        highlight_text,
        primary,
        radius_sm ,
    } = useTheme();

    const { successToast } = useToast();

    const dallarPerPoint = parseFloat(merchant?.dollars_per_point || '0.0');
    const pointToDollar = convertPoints / dallarPerPoint;
    const [isConfirmState, setIsConfirmState] = useState(false);

    const {
        mutate: convertPointsToDollar,
        isLoading,
        error: pointToCashError
    } = usePointsToCashGiftCard();

    const isPointsToDollarDiabled = pointToDollar === 0 || isLoading || pointsError !== '' || !loyaltyCard;

    useEffect(() => {

        pointToCashError && setSubmitError(t('general.submitError'));
    }, [pointToCashError]);

    const onAddPointsToCash = () => {

        const cardNumber = loyaltyCard?.card_number;

        if (!cardNumber) {
            setLoyaltyCardError(t('giftCard.pointsConvertor.selectRoyaltyError'));
        }

        if (cardNumber && member?.id) {

            convertPointsToDollar({
                member_id: member?.id.toString() || '',
                card_number: cardNumber,
                convert_points: convertPoints
            },
            {
                onSuccess: (_) => {

                    successToast(t('giftCard.pointsConvertor.reloadSuccess'));
                    observer.emit('close-actionsheet');
                }
            });
        }
    };

    useEffect(() => {

        if (giftCards && !giftCards.length) {
            observer.emit('open-actionsheet', GIFTCARD_FLOWS.addFunds);
        }
    }, [giftCards]);

    if (isConfirmState) {
        return (
            <VStack width={'100%'} alignItems={'center'} space={4}>
                <Caption
                    text={'giftCard.confirm_points.confirm_conversion'}
                    fontSize={24}
                />
                <Caption
                    text={'giftCard.confirm_points.review_the_redemption'}
                    textAlign={'center'}
                />
                <VStack space={4} w={'100%'}>
                    <VStack
                        space={2}
                        borderWidth={1}
                        borderRadius={radius_sm}
                        borderColor={primary}
                        p={4}
                    >
                        <HStack justifyContent={'space-between'}>
                            <Caption
                                text={'giftCard.confirm_points.total_points'}
                            />
                            <Caption>{ convertPoints }</Caption>
                        </HStack>
                        <Divider bg={primary} />
                        <HStack justifyContent={'space-between'}>
                            <Caption
                                text={'giftCard.confirm_points.total_load_amount'}
                                fontWeight={'bold'}
                            />
                            <Caption
                                fontWeight={'bold'}
                                color={highlight_text}
                            >
                                ${pointToDollar.toFixed(2)}
                            </Caption>
                        </HStack>
                    </VStack>
                    <VStack space={4}>
                        <Caption fontSize={'sm'} textAlign={'center'}>
                            <Caption text={'giftCard.confirm_points.payment_method'} />
                            <VStack px={2}>
                                <Caption
                                    text={'giftCard.confirm_points.points_conversion'}
                                    fontWeight={'bold'}
                                    textAlign={'center'}
                                />
                                { merchant?.dollars_per_point &&
                                <Caption
                                    text={'giftCard.confirm_points.points_per'}
                                    textValues={[1 / parseInt(merchant?.dollars_per_point)]}
                                    textAlign={'center'}
                                />
                                }
                            </VStack>
                        </Caption>
                    </VStack>
                    <Button
                        fontSize={18}
                        text={'giftCard.confirm_points.complete_conversion'}
                        onPress={onAddPointsToCash}
                        isDisabled={isLoading}
                    />
                </VStack>
            </VStack>
        );
    }

    return (
        <VStack space={5}>
            <Caption
                text={'giftCard.fund_with_points'}
                textValues={[merchant?.gift_card_alias]}
                alignSelf='center'
                fontSize={24}
            />
            <PointsConverterField t={t} onChange={(value) => setConvertPoints(value)} />
            <VStack>
                <Caption
                    text={'giftCard.select_card'}
                    textValues={[merchant?.gift_card_alias]}
                    fontSize={18}
                />
                <SelectLoyalty
                    loyaltyCard={loyaltyCard}
                    setLoyaltyCard={setLoyaltyCard}
                />
            </VStack>
            { loyaltyCardError &&
                <CaptionError>
                    { loyaltyCardError }
                </CaptionError>
            }
            { pointToCashError &&
                <CaptionError>
                    { (pointToCashError as any)?.data.message }
                </CaptionError>
            }
            <Button
                fontSize={18}
                text={t('general.add') + ` $${pointToDollar.toFixed(2)}`}
                onPress={() => setIsConfirmState(true)}
                disabled={isPointsToDollarDiabled}
            />
        </VStack>
    );
};

export default PointsConverter;
